/**
 * GDSCardDataService.js
 * Handles data processing, compatibility logic, and SKU derivation for cards.
 */
import { PROPAGATING_FIELDS } from './GDSModules.js';

export class GDSCardDataService {
    constructor(manager) {
        this.manager = manager;
    }

    get state() {
        return this.manager.state;
    }

    get api() {
        if (!this.manager.api) {
            throw new Error("GDSCardDataService: The API service is not available on the manager.");
        }
        return this.manager.api;
    }

    get renderer() {
        return this.manager.renderer;
    }

    async processMainCardGroups(mainCardsData) {
        const promises = Object.entries(mainCardsData).map(([cardTitle, cardData]) => {
            const defaultSKUs = this.getDefaultSKUs(cardData);
            if (!defaultSKUs.length) return Promise.resolve('');

            const initialSelections = this.findPathForSKUArray(cardData.options, defaultSKUs, cardData.dropdowns?.map(dd => Object.keys(dd)[0]));

            return this.createCardGroupHTML(cardTitle, cardData, defaultSKUs, initialSelections, true, false);
        });
        return Promise.all(promises);
    }

    async processAdditionalCardGroups(additionalCardsData, mainCardsData, isInitialLoad = false) {
        const propagatingValues = this.getPropagatingValuesFromMainCardDefs(mainCardsData);

        const promises = Object.entries(additionalCardsData).map(([cardTitle, cardData]) => {
            const { compatible, skus, selections } = this.determineAdditionalCardCompatibility(cardData, propagatingValues, isInitialLoad);

            if (!compatible || !skus.length) return Promise.resolve('');

            return this.createCardGroupHTML(cardTitle, cardData, skus, selections, false, true);
        });
        return Promise.all(promises);
    }

    async createCardGroupHTML(cardTitle, cardData, skus, initialSelections, isMain, isAdditional) {
        if (!skus.length) return '';
        try {
            const productsData = await Promise.all(skus.map(sku => this.api.fetchProductData(sku).catch(() => null)));
            return productsData.map((product, index) => {
                if (!product) return '';
                const selectionsForCard = (index === 0) ? initialSelections : { _isSecondaryProduct: true, _directSku: product.handle };
                return this.renderer.createProductCardHTML(product, cardTitle, cardData, isMain, isAdditional, index, selectionsForCard);
            }).join('');
        } catch (error) {
            console.error(`Error processing card group ${cardTitle}:`, error);
            return `<div class="p-4 text-red-500">Error loading products for ${cardTitle}.</div>`;
        }
    }

    determineAdditionalCardCompatibility(cardData, mainCardPropagatingValues, isInitialLoad = false) {
        // 1. Establish hard constraints from propagating values.
        const hardConstraints = { ...mainCardPropagatingValues };
        const allFields = cardData.dropdowns?.map(d => Object.keys(d)[0]) || [];

        // 2. High-level compatibility check. If the card can't ever support the required value, bail early.
        for (const [field, value] of Object.entries(hardConstraints)) {
            if (allFields.includes(field) && !this.cardDefinitionSupportsValue(cardData, field, value)) {
                return { compatible: false, skus: [], selections: null };
            }
        }

        // 3. Determine the card's OWN preferred default SKUs using the helper.
        const defaultSKUs = this.findDefaultSKUsForAdditionalCard(cardData, hardConstraints);
        if (!defaultSKUs.length) {
            return { compatible: false, skus: [], selections: null };
        }

        // 4. Find the ideal selection path for these default SKUs. This will be our starting point.
        const idealPath = this.findPathForSKUArray(cardData.options, defaultSKUs, allFields) || {};

        // 5. Now, attempt to find a complete, valid path.
        let finalSelectionsCandidate;

        if (isInitialLoad) {
            // On initial load, start with the card's own defaults and then overlay hard constraints
            // This ensures the card's defaults are respected when they don't conflict with propagating fields
            finalSelectionsCandidate = { ...idealPath };

            // Only override with hard constraints for fields that actually need to be constrained
            for (const [field, value] of Object.entries(hardConstraints)) {
                if (allFields.includes(field)) {
                    finalSelectionsCandidate[field] = value;
                }
            }
        } else {
            // For updates after initial load, start with hard constraints and use idealPath as hint
            finalSelectionsCandidate = { ...hardConstraints };
        }

        // Handle simple cards with no dropdowns
        if (!allFields.length) {
            return { compatible: true, skus: defaultSKUs, selections: {} };
        }

        // For validation, we need to ensure the path is complete and valid
        // Use the first propagating field as the anchor, or the first field if no propagating fields
        const anchorField = allFields.find(f => hardConstraints.hasOwnProperty(f)) || allFields[0];
        const anchorValue = finalSelectionsCandidate[anchorField];

        if (!anchorField) {
            return { compatible: true, skus: defaultSKUs, selections: {} };
        }

        // Validate and complete the path
        const pathFound = this.findCompleteValidPath(
            cardData,
            anchorField,
            anchorValue,
            finalSelectionsCandidate, // This object will be mutated with the final path
            allFields,
            idealPath // Still provide as hint for any missing fields
        );

        if (pathFound) {
            // 6. Success. Re-derive the SKUs from this confirmed path to be 100% certain they are correct.
            const finalSKUs = this.deriveProductSKUs(cardData, finalSelectionsCandidate);
            if (finalSKUs && finalSKUs.length > 0) {
                return { compatible: true, skus: finalSKUs, selections: finalSelectionsCandidate };
            }
        }

        // 7. If no path could be found, the card is ultimately incompatible.
        return { compatible: false, skus: [], selections: null };
    }

    findPathForSKUArray(optionsNode, targetSKUArray, fields, fieldIndex = 0, currentPath = {}) {
      // Ensure targetSKUArray is always treated as an array for comparison
      const safeTargetSKUArray = Array.isArray(targetSKUArray) ? targetSKUArray : [targetSKUArray];

      if (!optionsNode || typeof optionsNode !== 'object') {
        return null; // Invalid node
      }

      // --- Base Case: Found an array at the current node ---
      if (Array.isArray(optionsNode)) {
        // Sort both for consistent comparison
        const sortedNodeArray = [...optionsNode].sort();
        const sortedTargetArray = [...safeTargetSKUArray].sort();
        const nodeStr = JSON.stringify(sortedNodeArray);
        const targetStr = JSON.stringify(sortedTargetArray);

        if (nodeStr === targetStr) {
          // Exact match found! Return a *copy* of the path built so far.
          const finalPath = {};
          for (let i = 0; i < fieldIndex; i++) {
            if (currentPath.hasOwnProperty(fields[i])) {
              finalPath[fields[i]] = currentPath[fields[i]];
            }
          }
          return finalPath;
        } else {
          // Array found, but doesn't match the target
          return null;
        }
      }

      // --- Error Case: Reached end of fields definition but current node is not an array ---
      if (fieldIndex >= fields.length) {
        return null;
      }

      // --- Recursive Step ---
      const currentFieldName = fields[fieldIndex];
      if (!currentFieldName) {
        console.warn(`[findPath] Field name is undefined at index ${fieldIndex}. Fields:`, fields);
        return null; // Should not happen if fields array is correct
      }

      // Iterate through keys (option values) at the current level
      for (const value in optionsNode) {
        if (optionsNode.hasOwnProperty(value)) {
          const nextNode = optionsNode[value];
          currentPath[currentFieldName] = value; // Add current value to path temporarily

          const result = this.findPathForSKUArray(nextNode, safeTargetSKUArray, fields, fieldIndex + 1, currentPath);

          if (result) {
            // Match found down this branch
            return result;
          }

          // Backtrack
          delete currentPath[currentFieldName];
        }
      }

      // If no value at this level led to the target SKU array
      return null;
    }

    async updateCardWithNewFieldValue(card, field, value) {
        const cardIdentifier = card.getAttribute('data-product-handle') || card.getAttribute('data-card-title') || 'Unknown Card';
        if (this.isCardProtectedFromPropagation(card) && field !== "_NO_FIELD_CHANGE_") {
            // Allow propagation for specific fields if needed, otherwise return
            // Use case-insensitive comparison to handle variations in field naming
            const isPropagatingField = PROPAGATING_FIELDS.some(pf =>
                pf.toLowerCase() === field.toLowerCase()
            );
            if (!isPropagatingField) {
                return;
            }
        }

        const cardData = this.getCurrentCardData(card);
        if (!cardData || !cardData.dropdowns || !cardData.options) {
            console.warn(`[updateCardWNFV] Missing card data for card '${cardIdentifier}'. Cannot process update. Removing group.`);
            this.removeCardGroup(card);
            return;
        }
        const allFields = cardData.dropdowns.map(dropdown => Object.keys(dropdown)[0]);
        const fieldIndex = allFields.indexOf(field);

        if (fieldIndex === -1 && field !== "_NO_FIELD_CHANGE_") {
            console.warn(`[updateCardWNFV] Field '${field}' not found in dropdowns for '${cardIdentifier}'. Skipping update.`);
            return;
        }

        let originalSelectionsOnCard = {};
        const originalSelectionsAttr = card.getAttribute('data-selections');
        if (originalSelectionsAttr) {
            try {
                originalSelectionsOnCard = JSON.parse(originalSelectionsAttr);
            } catch (e) {
                console.error(`[updateCardWNFV] Error parsing original selections from card '${cardIdentifier}':`, e);
            }
        }

        // Start with the card's current state
        let workingSelections = { ...originalSelectionsOnCard };

        // Update working selections based on the incoming change and clear dependent fields
        if (field !== "_NO_FIELD_CHANGE_") {
            const previousValueForField = workingSelections[field];
            workingSelections[field] = value;

            if (previousValueForField !== value) {
                // This is where the path is broken and needs to be recalculated
                for (let i = fieldIndex + 1; i < allFields.length; i++) {
                    const dependentField = allFields[i];
                    if (workingSelections.hasOwnProperty(dependentField)) {
                        delete workingSelections[dependentField];
                    }
                }
            }
        }

        // Create a better hint for the pathfinder by looking up the default for the new state.
        // `workingSelections` currently holds the new hard constraints (e.g., the new Connection Type).
        let newOptimalHint = {};
        const newDefaultSKUs = this.findDefaultSKUsForAdditionalCard(cardData, workingSelections);
        if (newDefaultSKUs && newDefaultSKUs.length > 0) {
            const idealPathForNewDefault = this.findPathForSKUArray(cardData.options, newDefaultSKUs, allFields);
            if (idealPathForNewDefault) {
                // This is the ideal path based on the default config for the new constraints.
                newOptimalHint = idealPathForNewDefault;
            }
        }

        // Use the new hint if we found one; otherwise, fall back to the old selections as a weaker hint.
        const hintForPathFinder = Object.keys(newOptimalHint).length > 0 ? newOptimalHint : originalSelectionsOnCard;

        const baseChangedField = (field === "_NO_FIELD_CHANGE_") ? (allFields[0] || field) : field;
        const baseChangedValue = (field === "_NO_FIELD_CHANGE_") ? workingSelections[baseChangedField] : value;

        // Pass the new, smarter hint to the pathfinder. It will now try to follow the default path first.
        let pathFound = this.findCompleteValidPath(cardData, baseChangedField, baseChangedValue, workingSelections, allFields, hintForPathFinder);

        // Fallback logic if a valid path still isn't found
        if (!pathFound) {
            const conflictingFieldForFallback = "Connection Type";
            const requiredValueForFallback = (field === "Connection Type") ? value : workingSelections[conflictingFieldForFallback];

            if (requiredValueForFallback !== undefined) {
                const fallbackSelections = await this.attemptCaseTypeFallback(card, cardData, originalSelectionsOnCard, conflictingFieldForFallback, requiredValueForFallback, allFields);
                if (fallbackSelections) {
                    workingSelections = fallbackSelections;
                    pathFound = true;
                }
            }
        }

        // Handle the outcome
        if (!pathFound) {
            console.error(`[updateCardWNFV] CRITICAL: Still no valid path for '${cardIdentifier}' after field change and fallback. Removing card group.`);
            this.removeCardGroup(card);
            const cardTitle = card.getAttribute('data-card-title');
            if (this.state.context.main_cards && this.state.context.main_cards[cardTitle]) {
                this.handleRelatedCardsUpdate(field, undefined, card);
            }
            return;
        }

        // Successfully found a path, now update the card on the screen
        card.setAttribute('data-selections', JSON.stringify(workingSelections));
        let productSKUs = this.deriveProductSKUs(cardData, workingSelections);

        if (productSKUs && productSKUs.length > 0) {
            await this.fetchAndUpdateCardData(card, productSKUs[0], workingSelections);
            if (productSKUs.length > 1) {
                // Using the UI service to handle additional products is cleaner
                await this.manager.ui.handleAdditionalProducts(card, productSKUs.slice(1), workingSelections);
            } else {
                this.removeSecondaryCards(card);
            }
        } else {
            console.error(`[updateCardWNFV] CRITICAL: No product SKUs derived for '${cardIdentifier}' with selections:`, JSON.stringify(workingSelections), `despite pathFound being true. Removing card group.`);
            this.removeCardGroup(card);
            const cardTitle = card.getAttribute('data-card-title');
            if (this.state.context.main_cards && this.state.context.main_cards[cardTitle]) {
                this.handleRelatedCardsUpdate(field, undefined, card);
            }
        }
    }

    findDefaultSKUsForAdditionalCard(cardData, hardConstraints) {
        if (cardData.default && typeof cardData.default === 'object' && Object.keys(cardData.default).length > 0) {
            // First, try to find a default block matching a propagating field constraint.
            for (const field of PROPAGATING_FIELDS) {
                const value = hardConstraints[field];
                if (value !== undefined && cardData.default[value]) {
                    // Found a specific default for the propagating value (e.g., default["Next Gen"])
                    return cardData.default[value] || [];
                }
            }

            // If no match, fall back to the first key in the default object.
            const fallbackKey = Object.keys(cardData.default)[0];
            return cardData.default[fallbackKey] || [];
        }

        // Final fallback for simple cards with no default block but a simple options structure.
        return this.getDefaultSKUs(cardData);
    }

    async fetchAndUpdateCardData(card, sku, selections) {
        try {
            const product = await this.manager.api.fetchProductData(sku);
            if (product) {
                this.manager.ui.updateCardWithProductData(card, product, selections);
            } else {
                this.manager.ui.showErrorOnCard(card, 'Product not found');
            }
        } catch (error) {
            this.manager.ui.showErrorOnCard(card, 'Error loading data');
        }
    }

    /**
     * Gets the controlling values from the main card DEFINITIONS.
     * This should be called before rendering anything to the DOM.
     * @param {object} mainCardsData - The main_cards object from the context.
     * @returns {object} An object with propagating fields and their values.
     */
    getPropagatingValuesFromMainCardDefs(mainCardsData) {
        const propagatingValues = {};
        if (!mainCardsData) return propagatingValues;

        for (const field of PROPAGATING_FIELDS) {
            // Loop through the main card definitions from the context
            for (const cardTitle in mainCardsData) {
                const cardData = mainCardsData[cardTitle];
                // Check if this card definition uses the propagating field
                if (!cardData?.dropdowns?.some(d => Object.keys(d)[0] === field)) continue;

                // Derive the default selections for this main card purely from its data
                const defaultSKUs = this.getDefaultSKUs(cardData);
                if (defaultSKUs.length > 0) {
                    const fields = cardData.dropdowns.map(dd => Object.keys(dd)[0]);
                    const defaultSelections = this._deriveSelectionsFromSKU(cardData.options, defaultSKUs[0], fields) || {};

                    // If the default selection has a value for the propagating field, store it and move on
                    if (defaultSelections[field] !== undefined) {
                        propagatingValues[field] = defaultSelections[field];
                        break; // Found the value for this propagating field, go to the next field.
                    }
                }
            }
        }
        return propagatingValues;
    }

    getPropagatingValuesFromRenderedMainCards() {
        const propagatingValues = {};

        const mainCardsContainer = document.querySelector('#main-cards-container');
        if (!mainCardsContainer) return propagatingValues;

        for (const field of PROPAGATING_FIELDS) {
            const mainCards = mainCardsContainer.querySelectorAll('.product-card:not([data-secondary-index])');

            for (const card of mainCards) {
                const cardData = this.getCurrentCardData(card);
                if (!cardData?.dropdowns?.some(d => Object.keys(d)[0] === field)) continue;

                let selections = {};
                try {
                    const selectionsAttr = card.getAttribute('data-selections');
                    if (selectionsAttr) {
                        selections = JSON.parse(selectionsAttr);
                    }
                } catch (e) {
                    console.error('Error parsing selections:', e);
                }

                // If no selections on card, derive from default
                if (!selections[field] && cardData.default) {
                    const defaultSKUs = this.getDefaultSKUs(cardData);
                    if (defaultSKUs.length > 0) {
                        const fields = cardData.dropdowns.map(dd => Object.keys(dd)[0]);
                        selections = this.deriveSelectionsFromSKU(cardData.options, defaultSKUs[0], fields) || {};
                    }
                }

                if (selections[field] !== undefined) {
                    propagatingValues[field] = selections[field];
                    break; // Found value for this field
                }
            }
        }

        return propagatingValues;
    }

    getCurrentCardData(card) {
        const cardTitle = card?.dataset.cardTitle;
        if (!cardTitle) return null;
        return this.manager.state.context.main_cards?.[cardTitle] || this.manager.state.context.additional_cards?.[cardTitle] || null;
    }

    getDefaultSKUs(cardData) {
        if (!cardData?.default) return [];
        const defaultValues = Object.values(cardData.default);
        return (defaultValues.length > 0 && Array.isArray(defaultValues[0])) ? defaultValues[0] : [];
    }

    _deriveSelectionsFromSKU(optionsNode, targetSKU, fields, fieldIndex = 0, currentPath = {}) {
      if (fieldIndex >= fields.length || !optionsNode || typeof optionsNode !== 'object') {
        return null; // Reached end of fields or invalid node before finding array
      }

      const currentFieldName = fields[fieldIndex];

      // Base case: Check if the current node is the final product array
      if (Array.isArray(optionsNode)) {
        if (optionsNode.includes(targetSKU)) {
          return { ...currentPath }; // Found the SKU at this path
        } else {
          return null; // SKU not in this array
        }
      }

      // Recursive step: Iterate through keys (option values) at the current level
      for (const value in optionsNode) {
        if (optionsNode.hasOwnProperty(value)) {
          const nextNode = optionsNode[value];
          currentPath[currentFieldName] = value; // Add current value to path

          // Check if the next node is the final product array
          if (Array.isArray(nextNode)) {
            if (nextNode.includes(targetSKU)) {
              // Found it! Ensure no deeper selections exist in path than necessary
              const finalPath = {};
              for(let i = 0; i <= fieldIndex; i++) {
                finalPath[fields[i]] = currentPath[fields[i]];
              }
              return finalPath;
            }
            // If it's an array but doesn't contain the SKU, this path is wrong for this value.
            // Continue to the next value at this level.
          }
          // If the next node is an object, recurse deeper
          else if (typeof nextNode === 'object' && nextNode !== null) {
            const result = this._deriveSelectionsFromSKU(nextNode, targetSKU, fields, fieldIndex + 1, currentPath);
            if (result) {
              return result; // SKU found in a deeper branch
            }
          }
          // If nextNode is neither array nor object, or recursion returned null, backtrack.
          delete currentPath[currentFieldName]; // Remove current value before trying the next
        }
      }

      return null; // SKU not found under this branch
    }

    deriveProductSKUs(cardData, selections) {
        const cardTitleForLog = cardData ? (cardData.header_question || 'Unknown CardData') : 'No CardData'; // Get a title if possible
        if (!cardData || !cardData.options || !cardData.dropdowns) {
            console.warn('[deriveProductSKUs] Aborting: Missing cardData, options, or dropdowns.');
            return null;
        }
        const allFields = cardData.dropdowns.map(dropdown => Object.keys(dropdown)[0]);
        let currentNode = cardData.options;

        for (const field of allFields) {
            if (!currentNode || typeof currentNode !== 'object') {
                console.warn(`[deriveProductSKUs] Path broke: currentNode is not an object for field ${field}. Node:`, JSON.stringify(currentNode));
                return null;
            }
            if (Array.isArray(currentNode)) { // Should ideally be caught by loop condition if allFields is exhausted
                return currentNode;
            }

            const selection = selections[field];
            if (selection === undefined) {
                console.warn(`[deriveProductSKUs] Missing selection for field: ${field} in selections. Card: ${cardTitleForLog}`);
                return null;
            }
            if (!currentNode.hasOwnProperty(selection)) {
                console.warn(`[deriveProductSKUs] Path broke at field: ${field}, value: ${selection}. Node did not have this key. Node Keys:`, Object.keys(currentNode), `Card: ${cardTitleForLog}`);
                return null;
            }
            currentNode = currentNode[selection];
        }
        if (Array.isArray(currentNode)) {
            return currentNode;
        }
        console.warn(`[deriveProductSKUs] Path completed for ${cardTitleForLog} but did not end in a SKU array. Final node:`, JSON.stringify(currentNode));
        return null;
    }

    /**
     * Gets all possible values and currently valid values for a specific field.
     * This corrected version only considers "ancestor" selections to determine validity,
     * preventing circular dependency issues.
     */
    getPossibleValuesForField(cardData, field, currentSelections) {
        if (!cardData?.options || !Array.isArray(cardData.dropdowns)) {
            return { allValues: [], validValues: [] };
        }

        const allFieldNames = cardData.dropdowns.map(dd => Object.keys(dd)[0]);
        const targetFieldIndex = allFieldNames.indexOf(field);

        if (targetFieldIndex === -1) {
            return { allValues: [], validValues: [] };
        }

        // First, get ALL possible values for this field from the entire options tree.
        const allValues = this.getAllPossibleValuesForField(cardData.options, field, cardData.dropdowns);

        // Create a new object containing only the selections for fields that come *before* the current one.
        const ancestorSelections = {};
        for (let i = 0; i < targetFieldIndex; i++) {
            const ancestorField = allFieldNames[i];
            if (currentSelections[ancestorField] !== undefined) {
                ancestorSelections[ancestorField] = currentSelections[ancestorField];
            }
        }

        // Now, filter all possible values to find which ones are valid based ONLY on the ancestor selections.
        const validValues = allValues.filter(potentialValue => {
            // Create a test selection path including the ancestors and the potential value.
            const testSelections = { ...ancestorSelections, [field]: potentialValue };
            // Check if this partial path can lead to any product.
            return this.selectionPathLeadsToProduct(cardData.options, testSelections, allFieldNames);
        });

        return { allValues, validValues };
    }

    getAllPossibleValuesForField(optionsTree, targetFieldName, fieldDefinitions) {
      // Convert field definitions to an array of field names in order
      const fieldNames = fieldDefinitions.map(dropdown => Object.keys(dropdown)[0]);

      // Find the index of the target field
      const targetFieldIndex = fieldNames.indexOf(targetFieldName);

      if (targetFieldIndex === -1) {
        console.warn(`Field ${targetFieldName} not found in dropdown definitions`);
        return [];
      }

      // Set to store unique values
      const valuesSet = new Set();

      /**
       * Recursive function to traverse the options tree
       * @param {Object} node - Current node in the options tree
       * @param {number} depth - Current depth in the tree
       */
      function traverseTree(node, depth) {
        if (depth === targetFieldIndex) {
          // We've reached the level corresponding to the target field
          if (node && typeof node === 'object' && !Array.isArray(node)) {
            // Add all keys at this level to the values set
            Object.keys(node).forEach(key => valuesSet.add(key));
          }
          return;
        }

        if (!node || typeof node !== 'object' || Array.isArray(node)) {
          // We've hit a leaf node or invalid node before reaching the target depth
          return;
        }

        // Continue traversing all branches
        Object.keys(node).forEach(key => {
          traverseTree(node[key], depth + 1);
        });
      }

      // Start traversal from the root
      traverseTree(optionsTree, 0);

      // Convert set to array and return
      return Array.from(valuesSet);
    }

    selectionPathLeadsToProduct(optionsTree, selections, fieldOrder) {
      let currentNode = optionsTree;

      // Traverse the options tree based on selections
      for (let i = 0; i < fieldOrder.length; i++) {
        const field = fieldOrder[i];
        const value = selections[field];

        // If we've reached a leaf node (array of SKUs), we found a product path
        if (Array.isArray(currentNode)) {
          return true;
        }

        // If not an object or null, invalid path
        if (!currentNode || typeof currentNode !== 'object') {
          return false;
        }

        // If no selection for this field, we need to check if ANY branch leads to a product
        if (!value) {
          // Try each possible value at this level
          for (const key in currentNode) {
            if (this.selectionPathLeadsToProduct(currentNode[key], selections, fieldOrder.slice(i + 1))) {
              return true;
            }
          }
          return false;
        }

        // If the selection doesn't exist in the tree, invalid path
        if (!currentNode.hasOwnProperty(value)) {
          return false;
        }

        // Move to the next level
        currentNode = currentNode[value];
      }

      // If we've gone through all fields, check if we've reached a product array
      return Array.isArray(currentNode);
    }

    cardDefinitionSupportsValue(cardData, field, value) {
      // Check if the card data itself exists and has the necessary options structure
      if (!cardData || !cardData.options || !cardData.dropdowns) {
        // Don't log error here, it's handled upstream. Just return false.
        return false; // Cannot support if data is missing or incomplete
      }

      // Use the existing helper function to get ALL possible values (keys) for this field
      // This checks the *keys* at the appropriate level in the options tree.
      const allPossibleValues = this.getAllPossibleValuesForField(cardData.options, field, cardData.dropdowns);

      // Check if the incoming value exists in the list of all possible values for that field.
      const supports = allPossibleValues.includes(value);

      return supports;
    }

    isCardProtectedFromPropagation(card) {
      if (!card) return true; // Skip if card is invalid

      // Check data attributes first (most reliable)
      if (card.hasAttribute('data-is-secondary-product') ||
          card.hasAttribute('data-direct-sku') ||
          card.hasAttribute('data-no-propagate') ||
          card.hasAttribute('data-isolated-card') ||
          card.hasAttribute('data-secondary-index')) {
        return true;
      }

      // Check flags within data-selections as a fallback
      const selectionsAttr = card.getAttribute('data-selections');
      if (selectionsAttr) {
        try {
          const selections = JSON.parse(selectionsAttr);
          if (selections && (
              selections._isSecondaryProduct ||
              selections._directSku ||
              selections._noPropagate ||
              selections._isolatedCard)) {
            return true;
          }
        } catch (e) {
          // Ignore parsing errors, rely on attributes
        }
      }

      return false; // Not protected
    }

    hasVisibleOptions(cardData) {
        if (!cardData?.dropdowns) return false;

        let hasAnyMeaningfulChoice = false;

        // Check each dropdown to see if it offers a meaningful choice
        for (const dropdownObj of cardData.dropdowns) {
            const fieldName = Object.keys(dropdownObj)[0];

            // Skip propagating fields - they are hidden and auto-filled from main cards
            // Use case-insensitive comparison to handle variations in field naming
            const isPropagatingField = PROPAGATING_FIELDS.some(pf =>
                pf.toLowerCase() === fieldName.toLowerCase()
            );
            if (isPropagatingField) {
                continue;
            }

            const allPossibleValues = this.getAllPossibleValuesForField(cardData.options, fieldName, cardData.dropdowns);

            // Skip if no values found
            if (allPossibleValues.length === 0) continue;

            // Multiple options = choice to make (unless all are variations of N/A)
            if (allPossibleValues.length > 1) {
                // Check if all options are just "N/A" variations
                const nonNAValues = allPossibleValues.filter(val =>
                    val !== 'N/A' &&
                    val.toLowerCase() !== 'n/a'
                );

                if (nonNAValues.length > 1) {
                    // Multiple non-N/A options = meaningful choice
                    hasAnyMeaningfulChoice = true;
                    break;
                } else if (nonNAValues.length === 1) {
                    // One non-N/A option among N/A options might still be meaningful
                    // unless it's a default value like "1" or "Yes"
                    const singleNonNA = nonNAValues[0];
                    if (String(singleNonNA).trim() !== "1" &&
                        singleNonNA !== 'Yes' &&
                        singleNonNA.toLowerCase() !== 'yes') {
                        hasAnyMeaningfulChoice = true;
                        break;
                    }
                }
                // If all options are N/A variations, continue checking other fields
            } else if (allPossibleValues.length === 1) {
                // Single option - check if it's meaningful
                const singleValue = allPossibleValues[0];

                // Skip N/A, "1", "Yes" as these are typically default/only options
                if (singleValue !== 'N/A' &&
                    singleValue.toLowerCase() !== 'n/a' &&
                    String(singleValue).trim() !== "1" &&
                    singleValue !== 'Yes' &&
                    singleValue.toLowerCase() !== 'yes') {

                    // Single meaningful option might still be worth showing
                    // For example, if it's "Black" and there could be other colors
                    // But we need to check if this is the ONLY option across ALL fields

                    // Don't mark as meaningful yet, just continue checking
                }
            }
        }

        // If no meaningful choices found so far, do a final check:
        // Are ALL dropdowns either N/A or have only one trivial option?
        if (!hasAnyMeaningfulChoice) {
            // Count how many fields have only "N/A" or trivial single options
            let trivialFieldCount = 0;
            let totalFields = 0;

            for (const dropdownObj of cardData.dropdowns) {
                const fieldName = Object.keys(dropdownObj)[0];

                // Skip propagating fields when counting
                // Use case-insensitive comparison to handle variations in field naming
                const isPropagatingField = PROPAGATING_FIELDS.some(pf =>
                    pf.toLowerCase() === fieldName.toLowerCase()
                );
                if (isPropagatingField) {
                    continue;
                }

                totalFields++;
                const allPossibleValues = this.getAllPossibleValuesForField(cardData.options, fieldName, cardData.dropdowns);

                if (allPossibleValues.length === 0 ||
                    (allPossibleValues.length === 1 && (
                        allPossibleValues[0] === 'N/A' ||
                        allPossibleValues[0].toLowerCase() === 'n/a' ||
                        String(allPossibleValues[0]).trim() === "1" ||
                        allPossibleValues[0] === 'Yes' ||
                        allPossibleValues[0].toLowerCase() === 'yes'
                    ))) {
                    trivialFieldCount++;
                }
            }

            // If all fields are propagating fields (totalFields === 0), no edit button needed
            if (totalFields === 0) {
                return false;
            }

            // If all fields are trivial, hide the edit button
            if (trivialFieldCount === totalFields) {
                return false;
            }

            // Special case: If all fields are N/A except one (like Color: Black),
            // this is still not a meaningful choice
            if (trivialFieldCount === totalFields - 1) {
                // Check if the non-trivial field only has one option
                for (const dropdownObj of cardData.dropdowns) {
                    const fieldName = Object.keys(dropdownObj)[0];

                    // Skip propagating fields
                    // Use case-insensitive comparison to handle variations in field naming
                    const isPropagatingField = PROPAGATING_FIELDS.some(pf =>
                        pf.toLowerCase() === fieldName.toLowerCase()
                    );
                    if (isPropagatingField) {
                        continue;
                    }

                    const allPossibleValues = this.getAllPossibleValuesForField(cardData.options, fieldName, cardData.dropdowns);

                    // Found the non-trivial field
                    if (allPossibleValues.length === 1 &&
                        allPossibleValues[0] !== 'N/A' &&
                        allPossibleValues[0].toLowerCase() !== 'n/a') {
                        // Only one option in the only non-trivial field = no choice
                        return false;
                    }
                }
            }
        }

        return hasAnyMeaningfulChoice;
    }

    /**
     * Finds a complete valid path based on a user selection, prioritizing a previous path if possible.
     * This version correctly mutates the `selectionsToMutate` object with the full path upon success.
     *
     * @param {Object} cardData - The card's configuration data.
     * @param {string} changedField - The field that was just changed by the user.
     * @param {string} changedValue - The new value for the changed field.
     * @param {Object} selectionsToMutate - The selections object that will be modified IN-PLACE with the result.
     * @param {Array<string>} allFields - An ordered array of all field names for this card.
     * @param {Object} [previousSelections={}] - The selections before the change, used as a hint for auto-completion.
     * @returns {boolean} - True if a valid path was found, false otherwise.
     */
    findCompleteValidPath(cardData, changedField, changedValue, selectionsToMutate, allFields, previousSelections = {}) {
        // IMPORTANT: We must ensure the changedField keeps the changedValue
        // Store it so we can enforce it throughout the search
        const mustHaveField = changedField;
        const mustHaveValue = changedValue;

        // This recursive helper will try to find a path and will modify selectionsToMutate directly.
        const search = (node, fieldIndex) => {
            // BASE CASE: Success! We've reached a valid endpoint (an array of SKUs).
            if (Array.isArray(node)) {
                // But only if we've honored the user's selection
                if (selectionsToMutate[mustHaveField] === mustHaveValue) {
                    return true;
                } else {
                    // This path doesn't include the user's selection, so it's not valid
                    return false;
                }
            }

            // BASE CASE: Failure. We've run out of fields to check or hit an invalid part of the tree.
            if (fieldIndex >= allFields.length || !node || typeof node !== 'object') {
                return false;
            }

            const currentField = allFields[fieldIndex];
            const availableOptions = Object.keys(node);

            // CRITICAL FIX: If this is the field the user changed, we MUST use their value
            if (currentField === mustHaveField) {
                // Check if the user's value is even available
                if (!availableOptions.includes(mustHaveValue)) {
                    return false; // The user's selection is not valid at this point in the tree
                }

                // Force the user's selection
                selectionsToMutate[currentField] = mustHaveValue;

                // Continue searching with this value
                return search(node[mustHaveValue], fieldIndex + 1);
            }

            // For other fields, determine the order of options to try
            let optionsToTry = [];
            const preSelectedValue = selectionsToMutate[currentField];

            if (preSelectedValue !== undefined && availableOptions.includes(preSelectedValue)) {
                // A valid selection is already present for this field, so we only need to try that path.
                optionsToTry = [preSelectedValue];
            } else {
                // We need to auto-select a value for this field.
                // Prioritize the user's previous selection if it's still a valid option.
                const preferredOption = previousSelections[currentField];
                if (preferredOption && availableOptions.includes(preferredOption)) {
                    optionsToTry.push(preferredOption);
                    // Add the rest as fallbacks.
                    optionsToTry.push(...availableOptions.filter(opt => opt !== preferredOption));
                } else {
                    // No valid prior selection, just try all available options.
                    optionsToTry = availableOptions;
                }
            }

            // Recursively try the options for this level.
            for (const option of optionsToTry) {
                selectionsToMutate[currentField] = option; // Tentatively set the selection

                // If the recursive call finds a valid path, we're done.
                if (search(node[option], fieldIndex + 1)) {
                    return true;
                }
            }

            // If we get here, no option at this level led to a valid path. Backtrack.
            delete selectionsToMutate[currentField];
            return false;
        };

        // Start the search.
        const pathFound = search(cardData.options, 0);

        // This is the console log you added. It will now show the result correctly.
        // console.log(`[3] Path Finding Result: pathFound=${pathFound}. Final attempted path:`, JSON.parse(JSON.stringify(selectionsToMutate)));

        return pathFound;
    }

    findPathByAdjustingIntermediateFields(cardData, selectionsBeforeUserChange, userChangedField, userChangedValue, allFields) {
      const userChangedFieldIndex = allFields.indexOf(userChangedField);
      if (userChangedFieldIndex === -1) return null;

      // Iterate backwards from the field *just before* the one the user changed.
      // These are the fields we'll try to alter.
      for (let pivotFieldIndex = userChangedFieldIndex - 1; pivotFieldIndex >= 0; pivotFieldIndex--) {
        const pivotField = allFields[pivotFieldIndex];

        // Determine selections for fields *before* the current pivotField. These are fixed for this iteration.
        let basePathSelections = {};
        for (let i = 0; i < pivotFieldIndex; i++) {
          if (selectionsBeforeUserChange.hasOwnProperty(allFields[i])) {
            basePathSelections[allFields[i]] = selectionsBeforeUserChange[allFields[i]];
          } else {
            // If an ancestor selection is missing, this path is unlikely to work, but proceed cautiously.
            console.warn(`[SmartAdjust] Missing base selection for ancestor field ${allFields[i]} when pivoting on ${pivotField}`);
          }
        }

        // Get the options node for the current pivotField based on basePathSelections
        let currentOptionNode = cardData.options;
        for (let i = 0; i < pivotFieldIndex; i++) {
          const ancestorField = allFields[i];
          if (!currentOptionNode || typeof currentOptionNode !== 'object' || !basePathSelections[ancestorField] || !currentOptionNode[basePathSelections[ancestorField]]) {
            currentOptionNode = null;
            break;
          }
          currentOptionNode = currentOptionNode[basePathSelections[ancestorField]];
        }

        if (!currentOptionNode || typeof currentOptionNode !== 'object' || Array.isArray(currentOptionNode)) {
          continue; // Try the next earlier pivot field
        }

        const availablePivotOptions = Object.keys(currentOptionNode);

        for (const pivotOptionValue of availablePivotOptions) {
          // Construct a candidate starting point for findCompleteValidPath
          let candidateSelectionsForCompletion = {
            ...basePathSelections,
            [pivotField]: pivotOptionValue,
            [userChangedField]: userChangedValue // User's choice is fixed
          };
          // Fields between pivotField and userChangedField, and after userChangedField,
          // will be cleared/filled by findCompleteValidPath.
          // Use a deep copy for findCompleteValidPath to modify
          let tempAttemptSelections = JSON.parse(JSON.stringify(candidateSelectionsForCompletion));

          // findCompleteValidPath will try to fill fields from pivotFieldIndex + 1 onwards,
          // respecting the already set userChangedField.
          // The `changedField` for findCompleteValidPath should be the `userChangedField`
          // as that's the anchor for this attempt.
          // `previousSelections` for `findCompleteValidPath` can be `selectionsBeforeUserChange`
          // to guide filling fields *after* `userChangedField`.
          if (this.findCompleteValidPath(cardData, userChangedField, userChangedValue, tempAttemptSelections, allFields, selectionsBeforeUserChange)) {
            return tempAttemptSelections; // Found a complete valid path
          }
        }
      }

      return null;
    }

    async attemptCaseTypeFallback(card, cardData, originalSelectionsOnCard, conflictingField, requiredValue, allFields) {
      const cardIdentifier = card.getAttribute('data-product-handle') || card.getAttribute('data-card-title') || 'Unknown Card';

      const originalCaseType = originalSelectionsOnCard["Case Type"];

      if (!cardData.options || typeof cardData.options !== 'object' || !cardData.dropdowns.some(d => Object.keys(d)[0] === "Case Type")) {
        console.warn(`[FallbackAttempt] Card "${cardIdentifier}" does not have a configurable "Case Type" or options. Fallback not possible.`);
        return null;
      }

      const availableCaseTypes = Object.keys(cardData.options);
      const caseTypeIndex = allFields.indexOf("Case Type");

      for (const alternativeCase of availableCaseTypes) {
          if (alternativeCase === originalCaseType) {
              continue; // Skip the case type that already led to the conflict
          }

          // console.log(`[FallbackAttempt] Card: "${cardIdentifier}", Trying alternative Case Type: "${alternativeCase}"`);

          // 1. Construct a base selection state for this alternative case.
          //    Start with a fresh slate or original selections, then overlay critical changes.
          let tempSelections = { ...originalSelectionsOnCard }; // Start with a copy of the card's original state
          tempSelections["Case Type"] = alternativeCase;       // Force the new Case Type
          tempSelections[conflictingField] = requiredValue;    // Force the required value for the conflicting field

          // 2. Clear dependent fields to allow findCompleteValidPath to resolve them.
          //    If Case Type itself is the conflictingField, clear fields after it.
          //    If conflictingField is after Case Type, clear fields between them and after conflictingField.
          const conflictingFieldIndex = allFields.indexOf(conflictingField);

          for (let i = 0; i < allFields.length; i++) {
              const fieldNameToClear = allFields[i];

              // Don't clear the Case Type itself or the conflicting field we are trying to satisfy
              if (fieldNameToClear === "Case Type" || fieldNameToClear === conflictingField) {
                  continue;
              }

              // Clear fields that are dependent on the Case Type change or the conflicting field value
              if (caseTypeIndex !== -1 && i > caseTypeIndex) { // If field is after "Case Type"
                  if (tempSelections.hasOwnProperty(fieldNameToClear)) {
                      delete tempSelections[fieldNameToClear];
                  }
              } else if (conflictingFieldIndex !== -1 && i > conflictingFieldIndex) { // If field is after the "conflictingField"
                    if (tempSelections.hasOwnProperty(fieldNameToClear)) {
                      delete tempSelections[fieldNameToClear];
                  }
              }
          }
            // Re-ensure the critical values are set after clearing
          tempSelections["Case Type"] = alternativeCase;
          tempSelections[conflictingField] = requiredValue;

          // 3. Attempt to find a complete valid path with the new Case Type and the required conflicting field value.
          //    'changedField' for findCompleteValidPath should be 'conflictingField' to anchor the completion logic.
          if (this.findCompleteValidPath(cardData, conflictingField, requiredValue, tempSelections, allFields, {} /* No strong previous bias for this new case path */)) {
              return tempSelections; // tempSelections has been modified by findCompleteValidPath to a full valid path
          } else {
          }
      }
      return null;
    }

    getCurrentCardData(card) {
        const cardTitle = card?.dataset.cardTitle;
        if (!cardTitle) return null;
        return this.state.context.main_cards?.[cardTitle] ||
               this.state.context.additional_cards?.[cardTitle] || null;
    }

    removeSecondaryCards(mainCard) {
        const container = mainCard.closest('#main-cards-container, #additional-cards-container');
        const cardTitle = mainCard.getAttribute('data-card-title');
        if (!container || !cardTitle) return;

        const secondaryCards = Array.from(container.querySelectorAll(`.product-card[data-card-title="${cardTitle}"]`))
                                    .filter(c => c !== mainCard); // Get all except the main card

        if (secondaryCards.length > 0) {
            // console.log(`[removeSecondaryCards] Removing ${secondaryCards.length} secondary cards for group '${cardTitle}'.`);
            secondaryCards.forEach(sc => sc.remove());

            // Update field visibility after removal
            if (this.manager?.ui?.setupFieldVisibility) {
                this.manager.ui.setupFieldVisibility();
            }
        }
    }
}
